import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Dialog, IconButton, Tab, Tabs, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import * as Colors from '../theme/colors';
import AppLoader from '../components/AppLoader';
import SessionBar from '../components/SessionBar';
import {
  SessionSecurityControl,
  fetchSessionPolicy,
  streamProtocols,
  streamUrl,
} from '../services/contractors';
import SessionWatermark from '../components/SessionWatermark';

const MAX_RECONNECT_ATTEMPTS = 4;
const RECONNECT_BASE_MS = 1000;

const ContractorTerminal: React.FC = () => {
  const { isAuthenticated, isLoading, user, loginWithRedirect, getAccessTokenSilently } =
    useAuth0();

  useEffect(() => {
    if (isLoading || isAuthenticated) return;
    void loginWithRedirect({
      appState: { returnTo: `${window.location.pathname}${window.location.search}` },
    });
  }, [isLoading, isAuthenticated, loginWithRedirect]);
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const organization = params.get('organization') ?? '';
  const resource = params.get('resource') ?? '';
  const port = Number(params.get('port') ?? '22');

  const holder = useRef<HTMLDivElement>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const terminalRef = useRef<Terminal | null>(null);

  const [status, setStatus] = useState('Connecting');
  const [needsCredentials, setNeedsCredentials] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [method, setMethod] = useState<'password' | 'key'>('password');
  const [keyName, setKeyName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [connectedAt, setConnectedAt] = useState<number | null>(null);
  const [ended, setEnded] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [policy, setPolicy] = useState<SessionSecurityControl | null>(null);
  const [lostConnection, setLostConnection] = useState(false);
  const attempt = useRef(0);
  const retryTimer = useRef<number | null>(null);
  const finalRef = useRef(false);

  const readKeyFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      if (!text.includes('PRIVATE KEY')) {
        setError('That file does not look like a private key.');
        return;
      }
      setError(null);
      setPrivateKey(text);
      setKeyName(file.name);
    };
    reader.onerror = () => setError('That file could not be read.');
    reader.readAsText(file);
  }, []);

  const submitCredentials = useCallback(() => {
    socketRef.current?.send(
      JSON.stringify(
        method === 'key'
          ? { type: 'credentials', username, privateKey, passphrase }
          : { type: 'credentials', username, password }
      )
    );
    setPrivateKey('');
    setPassphrase('');
    setKeyName('');
    setPassword('');
    setNeedsCredentials(false);
    setStatus('Authenticating');
  }, [method, username, password, privateKey, passphrase]);

  useEffect(() => {
    if (!isAuthenticated || !organization) return;
    let cancelled = false;
    getAccessTokenSilently()
      .then((token) => fetchSessionPolicy(token, organization))
      .then((resolved) => {
        if (!cancelled) setPolicy(resolved);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, organization, getAccessTokenSilently]);

  useEffect(() => {
    const node = holder.current;
    if (!node || !policy) return;

    const access = policy.clipboardAccess;

    const blockOutbound = (event: ClipboardEvent) => {
      if (access !== 'twoWay') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const blockInbound = (event: ClipboardEvent) => {
      if (access === 'off') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    node.addEventListener('copy', blockOutbound, true);
    node.addEventListener('cut', blockOutbound, true);
    node.addEventListener('paste', blockInbound, true);

    return () => {
      node.removeEventListener('copy', blockOutbound, true);
      node.removeEventListener('cut', blockOutbound, true);
      node.removeEventListener('paste', blockInbound, true);
    };
  }, [policy]);

  const openSocket = useCallback(async () => {
    const terminal = terminalRef.current;
    if (!terminal) return;

    const token = await getAccessTokenSilently();
    const socket = new WebSocket(streamUrl(organization, resource, port), streamProtocols(token));
    socket.binaryType = 'arraybuffer';
    socketRef.current = socket;

    socket.onopen = () => {
      attempt.current = 0;
      setLostConnection(false);
      setEnded(false);
      setStatus('Connecting');
      terminal.options.disableStdin = false;
    };

    socket.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        terminal.write(new Uint8Array(event.data));
        return;
      }
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'credentials-required') {
          setNeedsCredentials(true);
          setStatus('Credential required');
        }
        if (message.type === 'ready') {
          setStatus('Connected');
          setConnectedAt(Date.now());
        }
        if (message.type === 'closed') {
          finalRef.current = true;
          setStatus('Disconnected');
          setError(message.reason ?? null);
        }
      } catch {
        terminal.write(event.data);
      }
    };

    socket.onerror = () => setError('The session could not be established.');

    socket.onclose = () => {
      socketRef.current = null;
      terminal.options.disableStdin = true;

      if (finalRef.current) {
        setStatus('Disconnected');
        setEnded(true);
        return;
      }

      if (attempt.current >= MAX_RECONNECT_ATTEMPTS) {
        setStatus('Disconnected');
        setEnded(true);
        setLostConnection(true);
        setError('The connection was lost. The remote shell has ended.');
        return;
      }

      const wait = RECONNECT_BASE_MS * 2 ** attempt.current;
      attempt.current += 1;
      setStatus('Reconnecting');
      retryTimer.current = window.setTimeout(() => {
        void openSocket().catch((e: Error) => setError(e.message));
      }, wait);
    };
  }, [getAccessTokenSilently, organization, resource, port]);

  const reconnect = useCallback(() => {
    finalRef.current = false;
    attempt.current = 0;
    setError(null);
    setLostConnection(false);
    void openSocket().catch((e: Error) => setError(e.message));
  }, [openSocket]);

  useEffect(() => {
    if (!isAuthenticated || !holder.current || terminalRef.current) return;

    const terminal = new Terminal({
      convertEol: true,
      cursorBlink: true,
      fontSize: 13,
      scrollback: 10000,
      theme: { background: Colors.Black, foreground: Colors.White },
    });
    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.open(holder.current);
    fit.fit();
    terminalRef.current = terminal;

    terminal.onData((data) => {
      const socket = socketRef.current;
      if (socket?.readyState === WebSocket.OPEN) socket.send(data);
    });

    void openSocket().catch((e: Error) => setError(e.message));

    const onResize = () => {
      fit.fit();
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(
          JSON.stringify({ type: 'resize', columns: terminal.cols, rows: terminal.rows })
        );
      }
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
      finalRef.current = true;
      socketRef.current?.close();
      terminal.dispose();
      terminalRef.current = null;
    };
  }, [isAuthenticated, openSocket]);

  useEffect(() => {
    if (connectedAt === null) return;
    setElapsed(Math.floor((Date.now() - connectedAt) / 1000));
    const timer = setInterval(
      () => setElapsed(Math.floor((Date.now() - connectedAt) / 1000)),
      1000
    );
    return () => clearInterval(timer);
  }, [connectedAt]);

  if (isLoading || !isAuthenticated) return <AppLoader />;

  const minutes = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const seconds = String(elapsed % 60).padStart(2, '0');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: Colors.Black, display: 'flex', flexDirection: 'column' }}>
      <SessionBar
        label="Terminal"
        online={Boolean(connectedAt)}
        status={status}
        elapsed={connectedAt ? `${minutes}:${seconds}` : undefined}
        onEnd={() => {
          finalRef.current = true;
          socketRef.current?.close();
          navigate('/portal');
        }}
        showFullscreen
        recording={policy?.enableSessionRecording}
      >
        {lostConnection && (
          <Button variant="outlined" size="small" onClick={reconnect}>
            Reconnect
          </Button>
        )}
      </SessionBar>

      {error && (
        <Typography sx={{ color: Colors.Red, fontSize: 13, px: 4, py: 1.5 }}>{error}</Typography>
      )}

      <Dialog
        open={needsCredentials}
        onClose={() => navigate('/portal')}
        PaperProps={{
          sx: {
            width: 520,
            maxWidth: 'calc(100vw - 48px)',
            bgcolor: '#16181D',
            border: `1px solid ${Colors.White}1F`,
            borderRadius: '16px',
            backgroundImage: 'none',
          },
        }}
      >
        <Box sx={{ px: 4, pt: 4, pb: 3.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Typography sx={{ color: Colors.White, fontSize: 24, fontWeight: 700 }}>
              Sign in to the terminal
            </Typography>
            <IconButton
              onClick={() => navigate('/portal')}
              sx={{ color: `${Colors.White}B3`, p: 0, mt: 0.5 }}
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Tabs
            value={method}
            onChange={(_, value) => setMethod(value)}
            sx={{
              mt: 2.5,
              minHeight: 40,
              borderBottom: `1px solid ${Colors.White}14`,
              mx: -4,
              px: 4,
            }}
          >
            <Tab
              disableRipple
              label="Password"
              value="password"
              sx={{
                textTransform: 'none',
                minHeight: 40,
                fontSize: 15,
                px: 0,
                mr: 3,
                color: `${Colors.White}8A`,
                '&.Mui-selected': { color: Colors.White },
              }}
            />
            <Tab
              disableRipple
              label="Private Key"
              value="key"
              sx={{
                textTransform: 'none',
                minHeight: 40,
                fontSize: 15,
                px: 0,
                color: `${Colors.White}8A`,
                '&.Mui-selected': { color: Colors.White },
              }}
            />
          </Tabs>

          <Typography sx={{ color: `${Colors.White}8A`, fontSize: 13, mt: 3, mb: 1 }}>
            Username
          </Typography>
          <TextField
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            sx={{ bgcolor: Colors.InputBackground, borderRadius: '8px' }}
          />

          {method === 'password' ? (
            <>
              <Typography sx={{ color: `${Colors.White}8A`, fontSize: 13, mt: 2.5, mb: 1 }}>
                Password
              </Typography>
              <TextField
                fullWidth
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitCredentials()}
                sx={{ bgcolor: Colors.InputBackground, borderRadius: '8px' }}
              />
            </>
          ) : (
            <>
              <Box
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  const file = event.dataTransfer.files?.[0];
                  if (file) readKeyFile(file);
                }}
                sx={{
                  border: `1px dashed ${
                    keyName || dragging ? Colors.Blue : `${Colors.White}2E`
                  }`,
                  bgcolor: dragging ? Colors.BlueFill : 'transparent',
                  borderRadius: '12px',
                  px: 3,
                  py: 4.5,
                  mt: 2.5,
                  textAlign: 'center',
                }}
              >
                {keyName ? (
                  <>
                    <Typography sx={{ color: Colors.White, fontSize: 18, fontWeight: 500, mb: 1 }}>
                      {keyName}
                    </Typography>
                    <Typography sx={{ color: `${Colors.White}8A`, fontSize: 14, mb: 1.5 }}>
                      Read in this browser. The file is not uploaded anywhere.
                    </Typography>
                    <Button
                      onClick={() => {
                        setPrivateKey('');
                        setKeyName('');
                      }}
                      sx={{ textTransform: 'none', fontSize: 14, color: Colors.White }}
                    >
                      Choose a different key
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography sx={{ color: `${Colors.White}8A`, fontSize: 18, mb: 2 }}>
                      Drop your key file here
                    </Typography>
                    <Button
                      component="label"
                      variant="outlined"
                      sx={{
                        textTransform: 'none',
                        fontSize: 15,
                        borderRadius: '999px',
                        px: 3,
                        py: 1.25,
                      }}
                    >
                      Choose key file
                      <input
                        type="file"
                        hidden
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) readKeyFile(file);
                          event.target.value = '';
                        }}
                      />
                    </Button>
                    <Typography sx={{ color: `${Colors.White}66`, fontSize: 13, mt: 2 }}>
                      A pem or OpenSSH key file
                    </Typography>
                  </>
                )}
              </Box>

              <Typography sx={{ color: `${Colors.White}8A`, fontSize: 13, mt: 2.5, mb: 1 }}>
                Key password, if it has one
              </Typography>
              <TextField
                fullWidth
                type="password"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                autoComplete="off"
                inputProps={{ 'data-1p-ignore': true, 'data-lpignore': true }}
                sx={{ bgcolor: Colors.InputBackground, borderRadius: '8px' }}
              />
              <Typography sx={{ color: `${Colors.White}66`, fontSize: 13, mt: 1.5 }}>
                Used for this session and then discarded. Zerac never stores your private key.
              </Typography>
            </>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            px: 4,
            py: 2.5,
            borderTop: `1px solid ${Colors.White}14`,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate('/portal')}
            sx={{ textTransform: 'none', borderRadius: '999px', minWidth: 108, py: 1.25 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitCredentials}
            disabled={!username || (method === 'key' ? !privateKey : !password)}
            sx={{ textTransform: 'none', borderRadius: '999px', minWidth: 108, py: 1.25 }}
          >
            Connect
          </Button>
        </Box>
      </Dialog>

      {ended && (
        <Box
          sx={{
            px: 4,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: Colors.Surface,
          }}
        >
          <Typography sx={{ color: Colors.White, fontSize: 14 }}>
            This session has ended. Anything typed now will not reach the machine.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => window.location.reload()}
            sx={{ textTransform: 'none', fontSize: 13 }}
          >
            Start a new session
          </Button>
        </Box>
      )}

      <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <Box
          ref={holder}
          sx={{ position: 'absolute', inset: 0, px: 2, py: 1, opacity: ended ? 0.5 : 1 }}
        />
        {policy && (
          <SessionWatermark
            watermarking={policy.watermarking}
            identity={user?.email || user?.name || ''}
            startedAt={connectedAt}
          />
        )}
      </Box>
    </Box>
  );
};

export default ContractorTerminal;
